import { useEffect, useMemo, useState } from "react";
import { useCachedResource } from "../hooks/useCachedResource";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PurchaseShell } from "../features/purchase/PurchaseShell";
import { useBranchScope } from "../features/purchase/branchScope";
import { useAuth } from "../context/AuthContext";
import { Toolbar } from "../components/data/Toolbar";
import { FilterRow } from "../components/data/FilterRow";
import { DataTable } from "../components/data/DataTable";
import { docColumns } from "../features/purchase/docColumns";
import { InvoiceUploadInput, useInvoiceUpload } from "../features/purchase/InvoiceUpload";
import { deletePurchaseDoc, listPurchaseDocs, listSuppliers } from "../api/purchases";
import { localName } from "../lib/localName";

const TAB_OF = { requisition: "requisitions", purchase_order: "orders", bill: "bills" };
const STATES = ["draft", "submitted", "pending_approval", "approved", "sent", "confirmed",
                "supplier_rejected", "partially_received", "received", "matched",
                "posted", "paid", "cancelled"];

const DocsBody = ({ docType }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { branchId } = useBranchScope();
  const { user } = useAuth();
  const isAdmin = (user?.role || "") === "admin";
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [attachingId, setAttachingId] = useState(null);
  const tab = TAB_OF[docType];

  useEffect(() => {
    const id = setTimeout(() => setQ(search), 250);
    return () => clearTimeout(id);
  }, [search]);

  const { data, reload: load } = useCachedResource(
    `purch-docs-${docType}-${branchId}-${state}-${supplierId}-${q}`,
    () => listPurchaseDocs({ doc_type: docType, search: q || undefined, limit: 100,
      state: state || undefined, supplier_id: supplierId || undefined,
      branch_id: branchId || undefined }).then((d) => d.items || []).catch(() => []),
    [docType, state, supplierId, branchId, q],
  );
  const items = data || [];

  useEffect(() => {
    listSuppliers({ limit: 200 }).then((d) => setSuppliers(d.items || []))
      .catch(() => setSuppliers([]));
  }, []);

  const uploader = useInvoiceUpload(() => load());
  const onAttach = (doc) => { setAttachingId(doc.id); uploader.pick(); };
  const onDelete = async (doc) => {
    if (!window.confirm(t("purchases.delete.confirm", { number: doc.number || "" }))) return;
    try {
      await deletePurchaseDoc(doc.id); toast.success(t("purchases.delete.done")); load();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(code ? t(`errors.${code}`, { defaultValue: t("errors.generic") }) : t("errors.generic"));
    }
  };
  const columns = useMemo(
    () => docColumns({ t, i18n, onOpen: (doc) => navigate(`/purchases/docs/${doc.id}`),
                       onAttach: docType === "purchase_order" ? onAttach : undefined,
                       onDelete: isAdmin ? onDelete : undefined }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n, navigate, isAdmin, docType]);

  return (
    <>
      <Toolbar testid="purchase-toolbar" search={search} onSearch={setSearch}
        actionLabel={t(`purchases.new.${docType}`, "")}
        onAction={["requisition", "purchase_order"].includes(docType)
          ? () => navigate(`/purchases/${tab}/new`) : undefined} />
      <FilterRow testid="purchase-filters" filters={[
        { key: "state", value: state, onChange: setState,
          allLabel: t("purchases.filters.allStates"),
          options: STATES.map((k) => ({ value: k, label: t(`purchases.states.${k}`, k) })) },
        { key: "supplier", value: supplierId, onChange: setSupplierId,
          allLabel: t("purchases.filters.allSuppliers"),
          options: suppliers.map((r) => ({ value: r.id,
            label: localName(r) })) },
      ]} />
      <DataTable testid="purchase-docs-table" columns={columns} rows={items}
        rowKey={(doc) => doc.id}
        onRowClick={(doc) => navigate(`/purchases/docs/${doc.id}`)}
        empty={search ? t("purchases.noSearchResults") : t("purchases.noDocs")} />
      <InvoiceUploadInput inputRef={uploader.ref}
        onChange={(e) => uploader.change(e, attachingId)} />
    </>
  );
};

export default function PurchaseDocsPage({ docType }) {
  return <PurchaseShell titleKey={TAB_OF[docType]}><DocsBody docType={docType} /></PurchaseShell>;
}
